/**
 * 会话ID解析工具（与后端 conversation/conv_id.go 保持一致）
 * 参考文档: docs/conversation-table-design.md
 */

/**
 * 解析会话ID类型
 * @param {string} convID - 会话ID
 * @returns {string} 'single' | 'group' | 'system' | 'unknown'
 */
export function parseConversationID(convID) {
    if (!convID) return 'unknown';
    
    if (convID.startsWith('SYSTEM:')) {
        return 'system';
    } else if (convID.startsWith('G:')) {
        return 'group';
    } else if (convID.startsWith('S:')) {
        return 'single';
    }
    return 'unknown';
}

/**
 * 从单聊会话ID中提取参与方
 * 注意：对于企微对企微会话（两个ID均以1688开头），无法仅通过前缀区分，
 * corpID 默认为排序靠前的ID。如需精确区分"自己"和"对方"，请使用 extractSelfAndOther。
 * @param {string} convID - 会话ID (格式: S:{tenant}:{id1}_{id2})
 * @returns {object} { corpID, personalID }
 */
export function extractParticipants(convID) {
    if (!convID) return { corpID: '', personalID: '' };
    
    const parts = convID.split(':');
    let ids = [];
    
    if (parts.length === 3) {
        ids = parts[2].split('_');
    } else if (parts.length === 2 && parts[0] === 'S') {
        // 兼容旧格式: S:1688xxx_788xxx
        ids = parts[1].split('_');
    }
    
    if (ids.length !== 2) {
        return { corpID: '', personalID: '' };
    }
    
    // 判断哪个是企微ID（1688开头）
    const id0IsCorp = ids[0].startsWith('1688');
    const id1IsCorp = ids[1].startsWith('1688');
    
    if (id0IsCorp && !id1IsCorp) {
        return { corpID: ids[0], personalID: ids[1] };
    } else if (!id0IsCorp && id1IsCorp) {
        return { corpID: ids[1], personalID: ids[0] };
    } else {
        // 企微对企微或其他情况：按排序顺序，第一个为 corpID
        return { corpID: ids[0], personalID: ids[1] };
    }
}

/**
 * 根据当前登录账号ID，从会话中提取"自己"和"对方"
 * 适用于企微对企微、企微对个微等所有单聊场景
 * @param {string} convID - 会话ID (格式: S:{tenant}:{id1}_{id2})
 * @param {string} selfID - 当前登录账号的VID（即 chat.source）
 * @returns {object} { selfID, otherID }
 */
export function extractSelfAndOther(convID, selfID) {
    if (!convID || !selfID) return { selfID: selfID || '', otherID: '' };
    
    const parts = convID.split(':');
    let ids = [];
    
    if (parts.length === 3) {
        ids = parts[2].split('_');
    } else if (parts.length === 2 && parts[0] === 'S') {
        ids = parts[1].split('_');
    }
    
    if (ids.length !== 2) {
        return { selfID: selfID, otherID: '' };
    }
    
    if (ids[0] === selfID) {
        return { selfID: ids[0], otherID: ids[1] };
    } else if (ids[1] === selfID) {
        return { selfID: ids[1], otherID: ids[0] };
    }
    
    // selfID 不在会话ID中（异常情况），回退
    return { selfID: selfID, otherID: ids[0] === selfID ? ids[1] : ids[0] };
}

/**
 * 判断是否为企业微信用户
 * @param {string} userID - 用户ID
 * @returns {boolean}
 */
export function isCorpUser(userID) {
    return userID && userID.startsWith('1688');
}

/**
 * 判断用户角色
 * @param {string} userID - 用户ID
 * @returns {string} 'corp' | 'personal' | 'system'
 */
export function participantRole(userID) {
    if (!userID) return 'unknown';
    
    if (userID.startsWith('1688')) {
        return 'corp';
    } else if (['wx_system', 'sys', 'ai_bot'].includes(userID)) {
        return 'system';
    } else {
        return 'personal';
    }
}

/**
 * 从会话ID中提取租户ID
 * @param {string} convID - 会话ID
 * @returns {number} 租户ID
 */
export function extractTenantID(convID) {
    if (!convID) return 1;
    
    const parts = convID.split(':');
    if (parts.length >= 2) {
        const tenantID = parseInt(parts[1]);
        return isNaN(tenantID) ? 1 : tenantID;
    }
    return 1; // 默认租户
}

/**
 * 构建单聊会话ID（与后端 BuildSingleConvID 一致）
 * @param {number} tenantID - 租户ID
 * @param {string} id1 - 参与方1
 * @param {string} id2 - 参与方2
 * @returns {string} 会话ID
 */
export function buildSingleConvID(tenantID, id1, id2) {
    // 尝试数值排序
    const n1 = parseInt(id1);
    const n2 = parseInt(id2);
    
    let first, second;
    if (!isNaN(n1) && !isNaN(n2)) {
        // 数值排序
        if (n1 < n2) {
            first = id1;
            second = id2;
        } else {
            first = id2;
            second = id1;
        }
    } else {
        // 字符串字典序
        if (id1 < id2) {
            first = id1;
            second = id2;
        } else {
            first = id2;
            second = id1;
        }
    }
    
    return `S:${tenantID}:${first}_${second}`;
}

/**
 * 构建群聊会话ID
 * @param {number} tenantID - 租户ID
 * @param {string} roomConvID - 群会话ID
 * @returns {string} 会话ID
 */
export function buildGroupConvID(tenantID, roomConvID) {
    return `G:${tenantID}:${roomConvID}`;
}

/**
 * 构建系统会话ID
 * @param {number} tenantID - 租户ID
 * @param {string} sysScene - 系统场景 (announcement/notification/ai_bot/oplog)
 * @param {string} participantKey - 参与方标识
 * @returns {string} 会话ID
 */
export function buildSystemConvID(tenantID, sysScene, participantKey) {
    return `SYSTEM:${tenantID}:${sysScene}:${participantKey}`;
}

// 系统分组（固定，不可删除）：仅保留默认组作为未分组会话的兜底入口。
// "已删除/已拉黑"系统分组与 relationStatus 自动归组已移除，分组完全由客服手动维护
export const SYSTEM_GROUPS = {
  DEFAULT: { id: 0,  label: '默认组' },
};

/**
 * 返回当日日期 "YYYY-MM-DD"
 */
function todayDate() {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

/**
 * 生成自动备注: "showName-YYYY-MM-DD"
 * showName 为空时回退使用 fallbackID
 * 与后端 conversation.AutoRemark 逻辑一致
 */
export function autoRemark(showName, fallbackID) {
    const name = showName || fallbackID || '';
    return name + '-' + todayDate();
}

// 决议会话归属分组：仅由用户手动分组（groupLabelID）驱动，
// 未分组会话落默认组；不再消费 relationStatus（已删除/已拉黑自动归组已移除）
export function effectiveGroupID(chat) {
  if (!chat) return 0;
  if (chat.groupLabelID > 0) return chat.groupLabelID;
  return 0;
}

